import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { AmountFormat, CallableType, Currency, OrderDetail, OrderDetailSummary, RepoClassification, RepoType, User } from 'api'
import { Locale, Translation } from 'locale'
import { DecisionMakerHelper, getCollateralMarket, getDateEditorFormat, getExcludedTermOptionKeys, MarketHelper } from 'model'
import { NumericInput, NumericSpinner, TextInput } from 'view/components/inputs'
import {
  AssignableNameCellRenderer,
  axeTradeConditionKeyCreator,
  createActionsRenderer,
  createAxeTradeConditionRenderer,
  createGroupedActionRenderer,
  createMasterDetailRowColDef,
  dateFilterParams,
  gridOptionsBase,
  StandardGridColumnId,
  StatusCellRenderer,
  tradesGridGroupingRowColDef,
  TypedCellRendererParams,
  TypedContext,
  TypedEditableCallbackParams,
  TypedValueFormatterParams,
  TypedValueGetterParams,
  ValidationCellRenderer,
  withAllContext
} from 'view/components/datagrid'
import {
  AliasesCombobox,
  AliasesComboboxProps,
  BooleanSelectbox,
  CounterpartyMasterLegalAgreementTypeSelectbox,
  CurrencySelectbox,
  DecisionMakerSelectbox,
  EvergreenNoticeSelectbox,
  OrderMyEntityCombobox,
  PrimeBrokerSelectbox,
  ReferenceRateSelectbox,
  RepoTypeSelectbox,
  RFQTypeSelectbox,
  StagingCollateralCombobox,
  TermSelectbox,
  TradingDatePicker
} from 'view/shared/entity'
import {
  axeFilterValueFormatter,
  axeTradeConditionFilterTextGetter,
  booleanFormatter,
  callableTypeFormatter,
  canEditCallableType,
  canEditEvergreenNotice,
  canEditHaircut,
  canEditOrderBlockTrade,
  canEditOrderDetailBondPrice,
  canEditOrderDetailBookStrategy,
  canEditOrderDetailCollateralName,
  canEditOrderDetailCounterpartyName,
  canEditOrderDetailDirection,
  canEditOrderDetailEndDate,
  canEditOrderDetailFundId,
  canEditOrderDetailNotional,
  canEditOrderDetailRepoType,
  canEditOrderDetailStartConsideration,
  canEditOrderDetailStartConsiderationOverride,
  canEditOrderDetailStartDate,
  canEditOrderFee,
  canEditOrderRate,
  canEditOrderTargetFillNotional,
  canEditOrderTargetFillStartConsideration,
  canEditOrderTerm,
  canEditRfqType,
  canEditSecondaryCollateral,
  collectionFilterFormatter,
  collectionFormatter,
  collectionKeyCreator,
  containerKeyCreator,
  dateQuickFilterTextGetter,
  evergreenNoticeFormatter,
  evergreenNoticePeriodQuickfixFilterTextGetter,
  getFeeFormatOptions,
  getHaircutFormatOptions,
  getNotesFieldNameForDisplay,
  getNotionalFormatOptions,
  getRateFormatOptions,
  haircutTypeFormatter,
  hasOrderNestedLegs,
  isChildRow,
  makeCounterpartyInstructionValueGetter,
  makeCusipGetter,
  makeDateFormatter,
  makeFeeFormatter,
  makeHaircutFilterGetter,
  makeHaircutFormatter,
  makeHaircutQuickFilterTextGetter,
  makeIsinGetter,
  makeNotionalFilterGetter,
  makeNotionalFormatter,
  makeNumberFilterGetter,
  makeNumberFormatter,
  makeNumberQuickFilterTextGetter,
  makePrevRateFilterGetter,
  makeRateFilterGetter,
  makeRateFormatter,
  makeRateQuickFilterTextGetter,
  makeStringOrNumberFormatter,
  makeTimeFormatter,
  NOTE_INDEXES,
  notionalHeaderValueGetter,
  orderHasNonCommonCounterpartyInstructionValueSet,
  orderTermFormatter,
  orderTermQuickFilterTextGetter,
  repoClassificationFormatter,
  rfqTypeFormatter,
  stagingRowDataGetter,
  timeQuickFilterTextGetter
} from 'view/shared/datagrid'
import { OrderFieldNames, StagingGridColumnId } from './StagingGridColumnNames'
import { CollateralBasketColumnId, CollectionColumnId, ContainerCollectionColumnId } from 'view/routes/ColumnNames'
import { getStagingStatusColor, getStagingStatusText } from './StagingStatusRenderer'
import { StagingActionsButton } from './StagingActionsButton'
import { StagingAxeTradeConditionButtons } from './StagingAxeTradeConditionButtons'
import * as constants from 'view/styles/constants'
import { getDynamicDetailRowHeight } from 'view/shared/preAssignments'
import { isOrderDetail, isOrderFix, isOrderSummary, isSmartGroupOrderDetailSummary } from 'view/routes/requests/shared/utils'
import { CallableTypeSelectbox } from 'view/shared/entity/CallableTypeSelectbox'
import { FilteredCheckboxHeaderRenderer } from 'view/components/datagrid/renderers/FilteredCheckboxHeaderRenderer'
import { StagingSecondaryCollateralCombobox } from 'view/shared/entity/SecondaryCollateralSelectbox'
import { StockBorrowLendingSelectbox } from 'view/shared/entity/StockBorrowLendingSelectbox'
import { StagingPreAssignmentsSubGrid } from './StagingPreAssignmentsSubGrid'
import {
  BASE_ASSET_TYPE,
  BASE_COLLECTION_TYPE,
  BASE_DIRECTION,
  BASE_ISSUE,
  BASE_MARKET_TYPE,
  BASE_MIC,
  BASE_REPO_TYPE,
  BASE_ROE,
  BASE_ROS
} from 'view/shared/datagrid/baseColumns'
import { HaircutTypeSelectbox } from 'view/shared/entity/haircutOptions/HaircutTypeSelectbox'

function stopPastePropagation(evt: React.ClipboardEvent) {
  evt.stopPropagation()
}

const ALL_POSSIBLE_STATUSES = [
  Translation.TC_ALL,
  Translation.TC_READY,
  Translation.TC_UNPREPARED,
  Translation.TC_ERROR,
  Translation.TC_NONE
].map(translation => Locale.translate(translation))

const getOrderStatusAsString = (orderDetail?: OrderDetail) => (orderDetail ? getStagingStatusText(orderDetail.order.status) : null)

const makeNoteColumn = (index: string) => ({
  colId: `${StagingGridColumnId.ORDER_NOTE}_${index}`,
  headerName: Locale.translate(Translation.TC_NOTE, { noteIndex: parseInt(index, 10) + 1 }),
  valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.notes?.[index],
  width: 180,
  filter: 'agSetColumnFilter',
  equals: () => false,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: `${OrderFieldNames.NOTES}[${index}]`,
    rowDataGetter: stagingRowDataGetter
  },
  cellEditorParams: {
    inputComponent: TextInput,
    inputParams: () => ({
      onPaste: stopPastePropagation
    }),
    updateField: `${OrderFieldNames.NOTES}.${index}`,
    rowDataGetter: stagingRowDataGetter,
    validationField: `${OrderFieldNames.NOTES}[${index}]`
  },
  editable: isChildRow,
  lockPinned: true
})

const getVisibleColumns = (isLockFixOrganisation: boolean) => {
  const VISIBLE_COLUMNS = [
    {
      colId: StandardGridColumnId.CHECKBOX,
      headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
      headerComponent: FilteredCheckboxHeaderRenderer,
      headerComponentParams: {
        allPossibleStatuses: ALL_POSSIBLE_STATUSES,
        getStatusAsString: getOrderStatusAsString,
        dataTestId: 'StagingSelectAll'
      },
      checkboxSelection: true,
      width: 75,
      maxWidth: 75,
      minWidth: 75,
      pinned: 'left' as const,
      lockPinned: true,
      enableRowGroup: false,
      lockPosition: true,
      lockVisible: true,
      filter: false,
      resizable: false,
      sortable: false
    },
    tradesGridGroupingRowColDef,
    {
      colId: StandardGridColumnId.ACTIONS,
      headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
      equals: () => false,
      width: 184,
      minWidth: 184,
      cellClass: 'action-cell',
      headerComponent: withAllContext(createGroupedActionRenderer(StagingActionsButton)),
      headerComponentParams: {
        dataTestId: 'StagingHeaderActions'
      },
      cellRenderer: withAllContext(createActionsRenderer(StagingActionsButton)),
      pinned: 'left' as const,
      lockPinned: true,
      lockPosition: true,
      lockVisible: true,
      enableRowGroup: false,
      filter: false,
      sortable: false
    },
    createMasterDetailRowColDef({
      isRowMaster: (orderDetail: OrderDetail) => !!orderDetail?.order?.nestedLegs?.legs
    }),
    {
      colId: StagingGridColumnId.ORDER_STATUS,
      headerName: Locale.translate(Translation.TC_STATUS),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
        const status = stagingRowDataGetter(params)?.order?.status
        return status && getStagingStatusText(status)
      },
      width: 100,
      filter: 'agSetColumnFilter',
      equals: () => false,
      cellRenderer: StatusCellRenderer,
      cellRendererParams: {
        rowDataGetter: stagingRowDataGetter,
        getStatusColor: getStagingStatusColor,
        getFieldNameForDisplay: getNotesFieldNameForDisplay
      },
      lockVisible: true
    },
    {
      colId: CollectionColumnId.COLLECTION_NAME,
      headerName: Locale.translate(Translation.TC_COLLECTION_NAME),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.collection,
      keyCreator: collectionKeyCreator,
      valueFormatter: collectionFormatter,
      cellRenderer: AssignableNameCellRenderer,
      cellRendererParams: {
        unassignedLabel: Locale.translate(Translation.TC_COLLECTION_UNASSIGNED),
        dataTestId: 'StagingOrdersGridModel-CollectionName-AssignableNameCellRenderer'
      },
      width: 150,
      filter: 'agSetColumnFilter',
      hide: true,
      filterParams: {
        valueFormatter: collectionFilterFormatter
      }
    },
    {
      colId: StagingGridColumnId.COUNTERPARTY_NAME,
      headerName: Locale.translate(Translation.TC_COUNTERPARTY),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
        const counterparty = stagingRowDataGetter(params)?.counterparty
        return counterparty?.alias || counterparty?.publishedName
      },
      equals: () => false,
      cellEditorParams: {
        inputComponent: AliasesCombobox,
        inputParams: (orderDetail: OrderDetail): Partial<AliasesComboboxProps> => ({
          withPortal: true,
          isin: orderDetail.order.collateralId,
          bui: orderDetail.order.basketAllocation?.basketId,
          secondaryBasketId: orderDetail.secondaryCollateralBasket?.id,
          secondaryCollateralId: orderDetail.secondaryCollateral?.id,
          fundId: orderDetail.order.fundId,
          clearingHouseId: orderDetail.order.negotiationInstruction ? orderDetail.order.negotiationInstruction.clearingHouseId : null,
          settlementVenue: orderDetail.order.negotiationInstruction ? orderDetail.order.negotiationInstruction.settlementVenue : null,
          masterLegalAgreementType: orderDetail.order.masterLegalAgreementType,
          marketType: orderDetail.order.marketType,
          repoType: orderDetail.order.repoType,
          dataTestId: 'StagingOrdersGridModel-CounterpartyCombobox',
          canClear: true,
          onPaste: stopPastePropagation,
          validationIssues: orderDetail.validationIssues
        }),
        updateField: (order: OrderDetail | null) => {
          return order?.counterparty?.alias ? OrderFieldNames.ALIAS : OrderFieldNames.COUNTERPARTY_NAME
        },
        useLoseEquality: true
      },
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.COUNTERPARTY
      },
      editable: canEditOrderDetailCounterpartyName,
      width: 150,
      filter: 'agSetColumnFilter',
      enableRowGroup: false,
      lockPinned: true
    },
    {
      colId: StagingGridColumnId.ORDER_FUND_ID,
      headerName: Locale.translate(Translation.TC_MY_ENTITY),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
        stagingRowDataGetter(params)?.orderFund?.publishedName,
      width: 150,
      filter: 'agSetColumnFilter',
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.FUND_ID,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: OrderMyEntityCombobox,
        inputParams: (orderDetail: OrderDetail) => ({
          withPortal: true,
          orderDetail,
          dataTestId: 'StagingOrdersGridModel-MyEntityFundSelectbox',
          onPaste: stopPastePropagation
        }),
        updateField: OrderFieldNames.FUND_ID
      },
      editable: canEditOrderDetailFundId,
      lockPinned: true
    },
    {
      ...BASE_DIRECTION,
      colId: StagingGridColumnId.ORDER_DIRECTION,
      headerName: Locale.translate(Translation.TC_ABBREVIATED_DIRECTION),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.direction,
      width: 82,
      minWidth: 82,
      filter: 'agSetColumnFilter',
      cellRendererParams: (params: TypedCellRendererParams<OrderDetail | OrderDetailSummary>) => ({
        validationField: OrderFieldNames.DIRECTION,
        rowDataGetter: stagingRowDataGetter,
        informativeTooltip:
          isOrderFix(params?.data?.order) && isLockFixOrganisation ? Locale.translate(Translation.TC_FIX_FIELD_DISABLED) : undefined
      }),
      cellEditorParams: {
        ...BASE_DIRECTION.cellEditorParams,
        inputParams: (_orderDetail: OrderDetail, context: any) => ({
          withPortal: true,
          directionFormat: context.userPreference.directionFormat,

          dataTestId: 'StagedOrder-DirectionSelectbox',
          onPaste: stopPastePropagation
        }),

        rowDataGetter: stagingRowDataGetter,
        updateField: OrderFieldNames.DIRECTION
      },
      editable: (params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) => {
        const disableOrderFix = isOrderFix(params?.data?.order) && isLockFixOrganisation
        return canEditOrderDetailDirection(params, disableOrderFix)
      },
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: StagingGridColumnId.COLLATERAL_NAME,
      headerName: Locale.translate(Translation.TC_COLLATERAL),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
        const data = stagingRowDataGetter(params)
        return data?.collateral?.name ?? data?.collateralBasket?.name
      },
      width: 140,
      filter: 'agSetColumnFilter',
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: (params: TypedCellRendererParams<OrderDetail | OrderDetailSummary>) => ({
        validationField: OrderFieldNames.ISIN,
        rowDataGetter: stagingRowDataGetter,
        informativeTooltip:
          isOrderFix(params?.data?.order) && isLockFixOrganisation ? Locale.translate(Translation.TC_FIX_FIELD_DISABLED) : undefined
      }),
      cellEditorParams: {
        inputComponent: StagingCollateralCombobox,
        inputParams: (orderDetail: OrderDetail) => ({
          withPortal: true,
          orderDetail,
          onPaste: stopPastePropagation,
          isVirtualized: true,
          dataTestId: 'StagedOrder-CollateralNameInput'
        }),
        rowDataGetter: stagingRowDataGetter,
        updateField: (orderDetail: OrderDetail | OrderDetailSummary | null) =>
          orderDetail?.order?.collateralId ? OrderFieldNames.ISIN : OrderFieldNames.BASKET_ID
      },
      editable: (params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) => {
        const disableOrderFix = isOrderFix(params?.data?.order) && isLockFixOrganisation
        return canEditOrderDetailCollateralName(params, disableOrderFix)
      },
      lockPinned: true
    },
    {
      colId: StagingGridColumnId.SECONDARY_COLLATERAL_NAME,
      headerName: Locale.translate(Translation.TC_SECONDARY_COLLATERAL),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
        const data = stagingRowDataGetter(params)
        return data?.secondaryInstrumentName
      },
      width: 140,
      filter: 'agSetColumnFilter',
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.SECONDARY_BASKET_ID,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: StagingSecondaryCollateralCombobox,
        inputParams: (orderDetail: OrderDetail) => ({
          detail: orderDetail,
          withPortal: true,
          canClear: true,
          onPaste: stopPastePropagation,
          dataTestId: 'StagedOrder-SecondaryBasket'
        }),
        updateField: OrderFieldNames.SECONDARY_BASKET_ID
      },
      editable: canEditSecondaryCollateral,
      lockPinned: true
    },
    {
      colId: StagingGridColumnId.STOCK_BORROW_LENDING_TYPE,
      headerName: Locale.translate(Translation.TC_STOCK_BORROW_LENDING_TYPE),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
        const data = stagingRowDataGetter(params)
        return data?.order.stockBorrowingLendingType
      },
      width: 140,
      filter: 'agSetColumnFilter',
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.STOCK_BORROW_LENDING_TYPE,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: StockBorrowLendingSelectbox,
        inputParams: () => ({
          withPortal: true,
          onPaste: stopPastePropagation
        }),
        updateField: OrderFieldNames.STOCK_BORROW_LENDING_TYPE
      },
      editable: false,
      lockPinned: true
    },
    {
      colId: StagingGridColumnId.ORDER_NOTIONAL,
      headerValueGetter: notionalHeaderValueGetter,
      headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.notional,
      valueFormatter: makeNotionalFormatter(),
      filterValueGetter: makeNotionalFilterGetter(),
      width: 120,
      minWidth: 120,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.NOTIONAL,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: NumericInput,
        inputParams: (orderDetail: OrderDetail, context: TypedContext<OrderDetail>) => ({
          min: orderDetail.collateral?.ticketIncrement,
          step: orderDetail.collateral?.ticketIncrement,
          isInteger: context.userPreference.amountFormat === AmountFormat.ACTUAL,
          formatOptions: getNotionalFormatOptions(context.userPreference.amountFormat),
          dataTestId: 'StagingOrdersGridModel-NotionalNumericInput',
          onPaste: stopPastePropagation
        }),
        updateField: OrderFieldNames.NOTIONAL
      },
      editable: canEditOrderDetailNotional,
      lockPinned: true,
      enableRowGroup: false,
      cellClassRules: {
        'paren-on-negative-cell': (params: any) => params.node.group && params.value < 0
      }
    },
    {
      marryChildren: true,
      children: [
        {
          colId: StagingGridColumnId.ORDER_START_CONSIDERATION,
          headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
          valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
            const orderData = stagingRowDataGetter(params)?.order ?? null
            return orderHasNonCommonCounterpartyInstructionValueSet(orderData, 'repoType')
              ? Locale.translate(Translation.SC_MULTIPLE)
              : orderData?.startConsideration
          },
          valueFormatter: makeStringOrNumberFormatter({ minDp: 2 }),
          width: 150,
          type: 'numericColumn',
          filter: 'agNumberColumnFilter',
          equals: () => false,
          cellRenderer: ValidationCellRenderer,
          cellRendererParams: {
            validationField: OrderFieldNames.START_CONSIDERATION,
            rowDataGetter: stagingRowDataGetter
          },
          cellEditorParams: {
            inputComponent: NumericInput,
            inputParams: () => ({
              formatOptions: { minDp: 2 },
              dataTestId: 'StagingOrdersGridModel-StartConsiderationNumericInput',
              onPaste: stopPastePropagation
            }),
            updateField: OrderFieldNames.START_CONSIDERATION
          },
          editable: canEditOrderDetailStartConsideration,
          lockPinned: true,
          enableRowGroup: false
        },
        {
          colId: StagingGridColumnId.START_CONSIDERATION_OVERRIDE,
          headerName: Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE),
          valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
            const orderDetail = stagingRowDataGetter(params)

            if (orderDetail && isOrderDetail(orderDetail) && hasOrderNestedLegs(orderDetail)) {
              return null
            }

            return stagingRowDataGetter(params)?.order?.startConsiderationOverride
          },
          valueFormatter: booleanFormatter,
          width: 120,
          filter: 'agSetColumnFilter',
          equals: () => false,
          cellRenderer: ValidationCellRenderer,
          cellRendererParams: {
            validationField: OrderFieldNames.START_CONSIDERATION_OVERRIDE,
            rowDataGetter: stagingRowDataGetter
          },
          cellEditorParams: {
            inputComponent: BooleanSelectbox,
            inputParams: () => ({
              withPortal: true,
              dataTestId: 'StagingOrdersGridModel-StartConsiderationOverrideBooleanSelectbox',
              onPaste: stopPastePropagation
            }),
            updateField: 'order.startConsiderationOverride'
          },
          editable: canEditOrderDetailStartConsiderationOverride,
          lockPinned: true
        }
      ]
    },
    {
      colId: StagingGridColumnId.ORDER_TERM,
      headerName: Locale.translate(Translation.TC_TERM),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.term,
      valueFormatter: orderTermFormatter,
      getQuickFilterText: orderTermQuickFilterTextGetter,
      width: 100,
      minWidth: 100,
      filter: 'agSetColumnFilter',
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.TERM,
        rowDataGetter: stagingRowDataGetter,
        dataTestId: 'StagingOrdersGridModel-Term-ValidationCellRenderer'
      },
      cellEditorParams: {
        inputComponent: TermSelectbox,
        inputParams: (orderDetail: OrderDetail) => {
          const excludeOptionKeys = getExcludedTermOptionKeys(orderDetail)
          return {
            withPortal: true,
            readOnly: !orderDetail.order.currency,
            excludeOptionKeys,
            dataTestId: 'StagedOrder-TermSelectbox',
            onPaste: stopPastePropagation
          }
        },
        rowDataGetter: stagingRowDataGetter,
        updateField: OrderFieldNames.TERM
      },
      editable: canEditOrderTerm,
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: StagingGridColumnId.EVERGREEN_NOTICE_PERIOD,
      headerName: Locale.translate(Translation.TC_EVERGREEN_NOTICE_PERIOD),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
        return stagingRowDataGetter(params)?.order?.evergreenNoticePeriod ?? null
      },
      valueFormatter: evergreenNoticeFormatter,
      getQuickFilterText: evergreenNoticePeriodQuickfixFilterTextGetter,
      width: 100,
      minWidth: 100,
      filter: 'agSetColumnFilter',
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.EVERGREEN_NOTICE_PERIOD,
        rowDataGetter: stagingRowDataGetter,
        dataTestId: 'StagingOrdersGridModel-EvergreenNoticePeriod-ValidationCellRenderer'
      },
      cellEditorParams: {
        inputComponent: EvergreenNoticeSelectbox,
        rowDataGetter: stagingRowDataGetter,
        updateField: OrderFieldNames.EVERGREEN_NOTICE_PERIOD,
        inputParams: (orderDetail: OrderDetail) => {
          return {
            withPortal: true,
            readOnly: orderDetail.order.repoClassification === RepoClassification.FIXED,
            dataTestId: 'StagedOrder-EvergreenNoticeSelectbox',
            onPaste: stopPastePropagation
          }
        }
      },
      editable: canEditEvergreenNotice,
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: StagingGridColumnId.ORDER_START_DATE,
      headerName: Locale.translate(Translation.TC_START_DATE),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.startDate,
      valueFormatter: makeDateFormatter(),
      getQuickFilterText: dateQuickFilterTextGetter,
      width: 110,
      minWidth: 110,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.START_DATE,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: TradingDatePicker,
        inputParams: (orderDetail: OrderDetail, context: any) => ({
          withPortal: true,
          currency: orderDetail.order.currency,
          market: orderDetail.collateral && getCollateralMarket(orderDetail?.collateral),
          dateFormat: getDateEditorFormat(context.userPreference.dateFormat),
          readOnly: !orderDetail.order.currency,
          dataTestId: 'StagingOrdersGridModel-StartDateTradingDatePicker',
          onPaste: stopPastePropagation
        }),
        rowDataGetter: stagingRowDataGetter,
        updateField: OrderFieldNames.START_DATE
      },
      editable: canEditOrderDetailStartDate,
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: StagingGridColumnId.ORDER_END_DATE,
      headerName: Locale.translate(Translation.TC_END_DATE),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.endDate,
      valueFormatter: makeDateFormatter(),
      getQuickFilterText: dateQuickFilterTextGetter,
      width: 110,
      minWidth: 110,
      filter: 'agDateColumnFilter',
      ...dateFilterParams,
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.END_DATE,
        rowDataGetter: stagingRowDataGetter,
        dataTestId: 'StagingOrdersGridModel-EndDate-ValidationCellRenderer'
      },
      cellEditorParams: {
        inputComponent: TradingDatePicker,
        inputParams: (orderDetail: OrderDetail, context: any) => ({
          withPortal: true,
          currency: orderDetail.order.currency,
          dateFormat: getDateEditorFormat(context.userPreference.dateFormat),
          readOnly: !orderDetail.order.currency,
          dataTestId: 'StagingOrdersGridModel-EndDateTradingDatePicker',
          onPaste: stopPastePropagation
        }),
        rowDataGetter: stagingRowDataGetter,
        updateField: OrderFieldNames.END_DATE
      },
      editable: canEditOrderDetailEndDate,
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: StagingGridColumnId.MARKET_TRADE_DATE,
      headerName: Locale.translate(Translation.TC_MARKET_TRADE_DATE),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
        stagingRowDataGetter(params)?.order?.marketTradeDate,
      width: 80,
      filter: 'agSetColumnFilter',
      valueFormatter: makeDateFormatter()
    },
    {
      colId: StagingGridColumnId.AXE_ROLL_UNWIND,
      headerName: Locale.translate(Translation.TC_AXE_ROLL_UNWIND),
      valueGetter: ({ data }: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => data?.axeTradeCondition,
      getQuickFilterText: axeTradeConditionFilterTextGetter,
      valueFormatter: axeTradeConditionFilterTextGetter,
      equals: () => false,
      cellRenderer: withAllContext(createAxeTradeConditionRenderer(StagingAxeTradeConditionButtons)),
      keyCreator: axeTradeConditionKeyCreator,
      width: 130,
      filter: 'agSetColumnFilter',
      filterParams: { valueFormatter: axeFilterValueFormatter },
      enableRowGroup: false,
      lockPinned: true,
      sortable: false
    },
    {
      ...BASE_REPO_TYPE,
      colId: StagingGridColumnId.ORDER_REPO_TYPE,
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
        const detail = stagingRowDataGetter(params)
        const order = detail?.order ?? null

        if (isSmartGroupOrderDetailSummary(detail)) {
          return detail.repoType
        }

        return makeCounterpartyInstructionValueGetter('repoType', 'repoType')(order)
      },
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.REPO_TYPE,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: RepoTypeSelectbox,
        inputParams: () => ({
          withPortal: true,
          supportedRepoTypes: [RepoType.CLASSIC, RepoType.BSB, RepoType.STOCK_BORROWING_LENDING],
          dataTestId: 'StagingOrdersGridModel-RepoTypeSelectbox',
          onPaste: stopPastePropagation
        }),
        updateField: OrderFieldNames.REPO_TYPE
      },
      editable: canEditOrderDetailRepoType,
      lockPinned: true
    },
    {
      colId: StagingGridColumnId.COLLATERAL_ISIN_OR_BUI,
      headerName: Locale.translate(Translation.TC_ISIN_BUI),
      valueGetter: makeIsinGetter(stagingRowDataGetter),
      width: 120
    },
    {
      colId: StagingGridColumnId.COLLATERAL_CURRENCY,
      headerName: Locale.translate(Translation.TC_CURRENCY),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.currency,
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.CURRENCY,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: CurrencySelectbox,
        inputParams: () => ({
          permittedCurrencies: [Currency.GBP, Currency.EUR],
          withPortal: true,
          dataTestId: 'StagingOrdersGridModel-CurrencySelectbox',
          onPaste: stopPastePropagation
        }),
        updateField: OrderFieldNames.CURRENCY
      },
      editable: false,
      lockPinned: true,
      width: 90,
      filter: 'agSetColumnFilter'
    },
    {
      colId: StagingGridColumnId.INVESTMENT_DECISION_MAKER,
      headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
        const user = stagingRowDataGetter(params)?.investmentDecisionMaker
        return user && DecisionMakerHelper.getName(user)
      },
      equals: () => false,
      width: 180,
      minWidth: 180,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.INVESTMENT_DECISION_MAKER,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: DecisionMakerSelectbox,
        inputParams: () => ({
          withPortal: true,
          dataTestId: 'StagingOrdersGridModel-InvestmentDecisionMakerSelectbox',
          onPaste: stopPastePropagation,
          canClear: true
        }),
        updateField: OrderFieldNames.INVESTMENT_DECISION_MAKER
      },
      editable: isChildRow,
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: StagingGridColumnId.EXECUTION_DECISION_MAKER,
      headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
        stagingRowDataGetter(params)?.executionDecisionMaker &&
        DecisionMakerHelper.getName(stagingRowDataGetter(params)?.executionDecisionMaker as User),
      equals: () => false,
      width: 180,
      minWidth: 180,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.EXECUTION_DECISION_MAKER,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: DecisionMakerSelectbox,
        inputParams: () => ({
          dataTestId: 'StagingOrdersGridModel-ExecutionDecisionMakerSelectbox',
          withPortal: true,
          onPaste: stopPastePropagation,
          canClear: true
        }),
        updateField: OrderFieldNames.EXECUTION_DECISION_MAKER
      },
      editable: isChildRow,
      lockPinned: true,
      enableRowGroup: false
    },
    {
      colId: StagingGridColumnId.BLOCK_TRADE,
      headerName: Locale.translate(Translation.TC_BLOCK),
      valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.blockTrade,
      valueFormatter: booleanFormatter,
      width: 80,
      filter: 'agSetColumnFilter',
      equals: () => false,
      cellRenderer: ValidationCellRenderer,
      cellRendererParams: {
        validationField: OrderFieldNames.BLOCK_TRADE,
        rowDataGetter: stagingRowDataGetter
      },
      cellEditorParams: {
        inputComponent: BooleanSelectbox,
        inputParams: () => ({
          withPortal: true,
          dataTestId: 'StagingOrdersGridModel-BlockTradeBooleanSelectbox',
          onPaste: stopPastePropagation
        }),
        updateField: OrderFieldNames.BLOCK_TRADE
      },
      editable: canEditOrderBlockTrade,
      lockPinned: true
    }
  ]

  return VISIBLE_COLUMNS
}

const HIDDEN_COLUMNS = [
  {
    colId: StagingGridColumnId.ORDER_PREV_RATE,
    headerName: Locale.translate(Translation.TC_PREV_RATE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
      return stagingRowDataGetter(params)?.order.servicingTrigger?.previousRate
    },
    filterValueGetter: makePrevRateFilterGetter(),
    valueFormatter: makeRateFormatter(),
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: ContainerCollectionColumnId.CONTAINER_COLLECTION_ID,
    headerName: Locale.translate(Translation.TC_CONTAINER_COLLECTION_ID),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.containerCollection?.id,
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME,
    headerName: Locale.translate(Translation.TC_CONTAINER_COLLECTION_NAME),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.containerCollection,
    keyCreator: containerKeyCreator,
    valueFormatter: collectionFormatter,
    cellRenderer: AssignableNameCellRenderer,
    cellRendererParams: {
      unassignedLabel: Locale.translate(Translation.TC_COLLECTION_UNASSIGNED),
      hideUnassignedLabel: true,
      dataTestId: 'StagingOrdersGridModel-ContainerCollectionName-AssignableNameCellRenderer'
    },
    width: 150,
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: collectionFilterFormatter
    },
    hide: true
  },
  {
    colId: CollateralBasketColumnId.COLLATERAL_BASKET_DESCRIPTION,
    headerName: Locale.translate(Translation.TC_BASKET_DESCRIPTION),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
      stagingRowDataGetter(params)?.collateralBasket?.description,
    width: 220,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralBasketColumnId.COLLATERAL_BASKET_DBV_CLASS,
    headerName: Locale.translate(Translation.TC_DBV_CLASS),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
      stagingRowDataGetter(params)?.collateralBasket?.dbvClass,
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.ORDER_BOND_PRICE,
    headerName: Locale.translate(Translation.TC_COLLATERAL_PRICE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
      const orderData = stagingRowDataGetter(params)?.order ?? null
      const value = params.data?.order?.bondPrice
      return orderHasNonCommonCounterpartyInstructionValueSet(orderData, 'repoType') && !!value
        ? Locale.translate(Translation.SC_MULTIPLE)
        : value
    },
    valueFormatter: makeStringOrNumberFormatter({
      maxFractionDigits: constants.bondPriceMaxFractionDigits
    }),
    filterValueGetter: makeNumberFilterGetter(),
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.BOND_PRICE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: NumericInput,
      inputParams: (_orderDetail: OrderDetail) => ({
        min: 0,
        maxLength: constants.bondPriceMaxFieldLength,
        dataTestId: 'StagingOrdersGridModel-CollateralPriceNumericInput',
        onPaste: stopPastePropagation,
        formatOptions: {
          maxFractionDigits: constants.bondPriceMaxFractionDigits
        }
      }),
      updateField: OrderFieldNames.BOND_PRICE
    },
    editable: canEditOrderDetailBondPrice,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.ORDER_ACCRUED_INTEREST,
    headerName: Locale.translate(Translation.TC_COLLATERAL_ACCRUED_INTEREST),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.accruedInterest,
    valueFormatter: makeNumberFormatter({ minDp: 2 }),
    width: 180,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.ORDER_ID,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => params.data?.order?.orderId,
    headerName: Locale.translate(Translation.TC_RFQ_ID),
    width: 100,
    filter: 'agSetColumnFilter',
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.ORDER_AXE_ID,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => params.data?.axeId,
    headerName: Locale.translate(Translation.TC_AXE_ID),
    width: 100,
    filter: 'agSetColumnFilter',
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.ORDER_UPDATED_BY,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.lastUpdatedBy,
    width: 130,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.ORDER_CREATED_AT_DATE,
    headerName: Locale.translate(Translation.TC_CREATION_DATE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.createdAt,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter,
    width: 110,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.ORDER_CREATED_AT_TIME,
    headerName: Locale.translate(Translation.TC_CREATION_TIME),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.createdAt,
    valueFormatter: makeTimeFormatter(),
    getQuickFilterText: timeQuickFilterTextGetter,
    width: 110,
    filter: false,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.ORDER_CREATED_BY,
    headerName: Locale.translate(Translation.TC_ORIGINATOR),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.createdBy,
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.PRODUCT,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.product?.name,
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.RFQ_TYPE,
    headerName: Locale.translate(Translation.TC_RFQ_TYPE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.rfqType,
    valueFormatter: rfqTypeFormatter,
    width: 100,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.RFQ_TYPE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: RFQTypeSelectbox,
      inputParams: () => ({
        withPortal: true,
        dataTestId: 'StagingOrdersGridModel-RFQTypeSelectbox',
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.RFQ_TYPE,
      rowDataGetter: stagingRowDataGetter
    },
    editable: canEditRfqType,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.CALLABLE,
    headerName: Locale.translate(Translation.TC_CALLABLE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.callableType,
    valueFormatter: callableTypeFormatter,
    width: 80,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.CALLABLE_TYPE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: CallableTypeSelectbox,
      inputParams: () => ({
        withPortal: true,
        dataTestId: 'StagingOrdersGridModel-CallableTypeSelectbox',
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.CALLABLE_TYPE,
      rowDataGetter: stagingRowDataGetter
    },
    editable: canEditCallableType,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.CALLABLE_PERIOD_DAYS,
    headerName: Locale.translate(Translation.TC_CALLABLE_PERIOD),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
      stagingRowDataGetter(params)?.order?.callablePeriodDays,
    width: 150,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.CALLABLE_PERIOD_DAYS,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: NumericSpinner,
      inputParams: (orderDetail: OrderDetail) => ({
        min: 1,
        max: 90,
        disabled: orderDetail.order.callableType === CallableType.NONE,
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.CALLABLE_PERIOD_DAYS,
      rowDataGetter: stagingRowDataGetter
    },
    editable: canEditCallableType,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.ORDER_ABSOLUTE_CONSIDERATION,
    headerName: Locale.translate(Translation.TC_ABSOLUTE_CONSIDERATION),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
      const orderData = stagingRowDataGetter(params)?.order ?? null
      if (!orderData) {
        return null
      }
      return orderHasNonCommonCounterpartyInstructionValueSet(orderData, 'repoType')
        ? Locale.translate(Translation.SC_MULTIPLE)
        : isOrderSummary(orderData)
        ? orderData.absoluteConsideration
        : orderData.startConsideration
    },
    valueFormatter: makeStringOrNumberFormatter({ minDp: 2 }),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.START_CONSIDERATION,
      rowDataGetter: stagingRowDataGetter
    },
    editable: false,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.FUND_LEI,
    headerName: Locale.translate(Translation.TC_MY_ENTITY_LEI),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.orderFund?.lei,
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.ORDER_REFERENCE_RATE,
    headerName: Locale.translate(Translation.TC_REFERENCE_RATE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.referenceRate?.name,
    width: 120,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.REFERENCE_RATE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: ReferenceRateSelectbox,
      inputParams: (orderDetail: OrderDetail) => ({
        withPortal: true,
        dataTestId: 'StagingOrdersGridModel-ReferenceRateTypeSelectbox',
        referenceRateIds: Object.keys(orderDetail.productSector?.defaultFloatCrystallisationDaysByReferenceRateId || {}),
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.REFERENCE_RATE
    },
    editable: isChildRow,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.FLOAT_CRYSTALLISATION_DAYS,
    headerName: Locale.translate(Translation.TC_FLOAT_CRYSTALLISATION_DAYS),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
      stagingRowDataGetter(params)?.floatCrystallisationDays,
    width: 120,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRendererParams: {
      rowDataGetter: stagingRowDataGetter
    },
    editable: false,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.ORDER_TRADE_TYPE,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
      stagingRowDataGetter(params)?.order?.repoClassification,
    headerName: Locale.translate(Translation.TC_TRADE_TYPE),
    width: 100,
    filter: 'agSetColumnFilter',
    valueFormatter: repoClassificationFormatter
  },
  {
    colId: StagingGridColumnId.ORDER_RATE,
    headerName: Locale.translate(Translation.TC_RATE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => params.data?.order?.rate,
    valueFormatter: makeRateFormatter(),
    filterValueGetter: makeRateFilterGetter(),
    getQuickFilterText: makeRateQuickFilterTextGetter(),
    width: 100,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.RATE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: NumericInput,
      inputParams: (orderDetail: OrderDetail) => ({
        formatOptions: getRateFormatOptions(orderDetail.referenceRate),
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.RATE
    },
    editable: canEditOrderRate,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.ORDER_FEE,
    headerName: Locale.translate(Translation.TC_FEE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => params.data?.order?.fee,
    valueFormatter: makeFeeFormatter(),
    filterValueGetter: makeNumberFilterGetter(getFeeFormatOptions()),
    getQuickFilterText: makeNumberQuickFilterTextGetter(getFeeFormatOptions()),
    width: 100,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.FEE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: NumericInput,
      inputParams: () => ({
        formatOptions: getFeeFormatOptions(),
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.FEE
    },
    editable: canEditOrderFee,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.COLLATERAL_COUPON,
    headerName: Locale.translate(Translation.TC_COUPON),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => params.data?.collateral?.coupon,
    valueFormatter: makeNumberFormatter({ percent: true, minDp: 1, decimalShift: 2 }),
    filterValueGetter: makeNumberFilterGetter({ percent: true, minDp: 1, decimalShift: 2 }),
    getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true }),
    width: 80,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.ORDER_BOOK,
    headerName: Locale.translate(Translation.TC_BOOK),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.book,
    width: 180,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.BOOK,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: TextInput,
      inputParams: () => ({
        onPaste: stopPastePropagation,
        maxLength: constants.internalBookingMaxFieldLength
      }),
      updateField: OrderFieldNames.BOOK
    },
    editable: canEditOrderDetailBookStrategy,
    lockPinned: true
  },
  ...NOTE_INDEXES.map(makeNoteColumn),
  {
    colId: StagingGridColumnId.ORDER_STRATEGY,
    headerName: Locale.translate(Translation.TC_STRATEGY),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.strategy,
    width: 180,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.STRATEGY,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: TextInput,
      inputParams: () => ({
        onPaste: stopPastePropagation,
        maxLength: constants.internalBookingMaxFieldLength
      }),
      updateField: OrderFieldNames.STRATEGY
    },
    editable: canEditOrderDetailBookStrategy,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.ORDER_PRIME_BROKER,
    headerName: Locale.translate(Translation.TC_PRIME_BROKER),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.primeBroker?.name,
    width: 180,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.PRIME_BROKER,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: PrimeBrokerSelectbox,
      inputParams: () => ({
        withPortal: true,
        onPaste: stopPastePropagation
      }),
      forceUpdateOnNull: true,
      updateField: OrderFieldNames.PRIME_BROKER
    },
    editable: isChildRow,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.ORDER_BUNDLE,
    headerName: Locale.translate(Translation.TC_BUNDLE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.bundle,
    width: 180,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.BUNDLE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: TextInput,
      inputParams: () => ({
        onPaste: stopPastePropagation,
        maxLength: constants.internalBookingMaxFieldLength
      }),
      updateField: OrderFieldNames.BUNDLE
    },
    editable: isChildRow,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.COLLATERAL_ISSUER,
    headerName: Locale.translate(Translation.TC_ISSUER),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.collateral?.issuer,
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.COLLATERAL_CUSIP,
    headerName: Locale.translate(Translation.TC_CUSIP),
    valueGetter: makeCusipGetter(stagingRowDataGetter),
    width: 120
  },
  {
    colId: StagingGridColumnId.PRODUCT_SECTOR,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.productSector?.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.ORDER_MARKET,
    headerName: Locale.translate(Translation.TC_MARKET),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
      const market = stagingRowDataGetter(params)?.order.market
      return market && MarketHelper.getName(market)
    },
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.ORDER_EXTERNAL_ID,
    headerName: Locale.translate(Translation.TC_POSITION_ID),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.positionId,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.EXTERNAL_ID,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputParams: () => ({
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.EXTERNAL_ID
    },
    width: 100,
    editable: isChildRow,
    lockPinned: true
  },
  {
    ...BASE_COLLECTION_TYPE,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.collection?.type
  },
  {
    colId: CollectionColumnId.COLLECTION_ID,
    headerName: Locale.translate(Translation.TC_COLLECTION_ID),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.collection?.id,
    filter: 'agSetColumnFilter',
    width: 150
  },
  {
    colId: StagingGridColumnId.NOTICE_PERIOD_DAYS,
    headerName: Locale.translate(Translation.TC_NOTICE_PERIOD_DAYS),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.noticePeriodDays,
    width: 150,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.NOTICE_PERIOD_DAYS,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: NumericSpinner,
      inputParams: (orderDetail: OrderDetail) => ({
        min: 0,
        value: orderDetail.order.noticePeriodDays,
        isInteger: true,
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.NOTICE_PERIOD_DAYS
    },
    editable: isChildRow,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.UPLOAD_ROW_INDEX,
    headerName: Locale.translate(Translation.TC_UPLOAD_ROW_INDEX),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.uploadRowIndex,
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.ALLOW_PARTIAL,
    headerName: Locale.translate(Translation.TC_ALLOW_PARTIAL),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.allowPartial,
    valueFormatter: booleanFormatter,
    width: 120,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.ALLOW_PARTIAL,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: BooleanSelectbox,
      inputParams: () => ({
        withPortal: true,
        dataTestId: 'StagingOrdersGridModel-AllowPartialBooleanSelectbox',
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.ALLOW_PARTIAL
    },
    editable: isChildRow,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.ORDER_TARGET_FILL_NOTIONAL,
    headerName: Locale.translate(Translation.TC_TARGET_FILL_NOTIONAL),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => params.data?.order?.targetFillNotional,
    valueFormatter: makeNotionalFormatter(),
    filterValueGetter: makeNotionalFilterGetter(),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.TARGET_FILL_NOTIONAL,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: NumericInput,
      inputParams: (orderDetail: OrderDetail, context: TypedContext<OrderDetail>) => ({
        min: orderDetail.collateral?.ticketIncrement,
        step: orderDetail.collateral?.ticketIncrement,
        isInteger: context.userPreference.amountFormat === AmountFormat.ACTUAL,
        formatOptions: getNotionalFormatOptions(context.userPreference.amountFormat),
        dataTestId: 'StagingOrdersGridModel-TargetFillNotionalNumericInput',
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.TARGET_FILL_NOTIONAL
    },
    editable: canEditOrderTargetFillNotional,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.ORDER_TARGET_FILL_START_CONSIDERATION,
    headerName: Locale.translate(Translation.TC_TARGET_FILL_START_CONSIDERATION),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
      const orderData = stagingRowDataGetter(params)?.order ?? null
      const value = params.data?.order?.targetFillStartConsideration
      return orderHasNonCommonCounterpartyInstructionValueSet(orderData, 'repoType') && !!value
        ? Locale.translate(Translation.SC_MULTIPLE)
        : value
    },
    valueFormatter: makeStringOrNumberFormatter({ minDp: 2 }),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.TARGET_FILL_START_CONSIDERATION,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: NumericInput,
      inputParams: () => ({
        formatOptions: { minDp: 2 },
        dataTestId: 'StagingOrdersGridModel-TargetFillStartConsiderationNumericInput',
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.TARGET_FILL_START_CONSIDERATION
    },
    editable: canEditOrderTargetFillStartConsideration,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: StagingGridColumnId.PRE_AGREED,
    headerName: Locale.translate(Translation.TC_PRE_AGREED),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.preAgreed,
    valueFormatter: booleanFormatter,
    width: 120,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.PRE_AGREED,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: BooleanSelectbox,
      inputParams: () => ({
        withPortal: true,
        dataTestId: 'StagingOrdersGridModel-PreAgreedBooleanSelectbox',
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.PRE_AGREED
    },
    editable: isChildRow,
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.MASTER_LEGAL_AGREEMENT_TYPE,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
      const order = stagingRowDataGetter(params)?.order ?? null
      return makeCounterpartyInstructionValueGetter('masterLegalAgreementType', 'masterLegalAgreementType')(order)
    },
    headerName: Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENT_TYPE),
    width: 100,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.MASTER_LEGAL_AGREEMENT_TYPE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: CounterpartyMasterLegalAgreementTypeSelectbox,
      inputParams: (orderDetail: OrderDetail) => ({
        dataTestId: 'StagingOrdersGridModel-MasterLegalAgreementTypeSelectbox',
        withPortal: true,
        onPaste: stopPastePropagation,
        canClear: true,
        counterpartyIds: orderDetail.order.negotiationInstruction.counterpartyInstructions.map(
          counterpartyInstruction => counterpartyInstruction.counterpartyId
        ),
        fundId: orderDetail.order.fundId
      }),
      updateField: OrderFieldNames.MASTER_LEGAL_AGREEMENT_TYPE
    },
    editable: (params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) => {
      const order = stagingRowDataGetter(params)?.order ?? null
      return orderHasNonCommonCounterpartyInstructionValueSet(order, 'masterLegalAgreementType') ? false : isChildRow(params)
    },
    lockPinned: true
  },
  {
    colId: StagingGridColumnId.SETTLEMENT,
    headerName: Locale.translate(Translation.TC_SETTLEMENT),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
      stagingRowDataGetter(params)?.order?.negotiationInstruction?.settlementVenue,
    filter: 'agSetColumnFilter',
    width: 150
  },
  {
    colId: StagingGridColumnId.CSD,
    headerName: Locale.translate(Translation.TC_CSD),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
      stagingRowDataGetter(params)?.order?.negotiationInstruction?.csd,
    filter: 'agSetColumnFilter',
    width: 150
  },
  {
    colId: StagingGridColumnId.CLEARING_VENUE,
    headerName: Locale.translate(Translation.TC_CLEARING_VENUE),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.clearingHouseName,
    filter: 'agSetColumnFilter',
    width: 150
  },
  {
    colId: StagingGridColumnId.SPONSORING_BANK,
    headerName: Locale.translate(Translation.TC_SPONSORING_BANK),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.sponsoringBankName,
    filter: 'agSetColumnFilter',
    width: 150
  },
  {
    colId: StagingGridColumnId.LINKED_TRADE_ID,
    headerName: Locale.translate(Translation.TC_LINKED_TRADE_ID),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.linkedTradeId,
    filter: 'agSetColumnFilter',
    width: 150
  },
  {
    colId: StagingGridColumnId.MY_ENTITY_SHORT_NAME,
    headerName: Locale.translate(Translation.TC_MY_ENTITY_SHORT_NAME),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.orderFund?.shortName,
    filter: 'agSetColumnFilter',
    width: 150
  },
  {
    colId: StagingGridColumnId.MY_FICC_ENTITY_SHORT_NAME,
    headerName: Locale.translate(Translation.TC_MY_FICC_ENTITY_SHORT_NAME),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
      stagingRowDataGetter(params)?.orderFund?.ficcShortName,
    filter: 'agSetColumnFilter',
    width: 150
  },
  {
    colId: StagingGridColumnId.FACTOR,
    headerName: Locale.translate(Translation.TC_FACTOR),
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.factor,
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    ...BASE_ASSET_TYPE,
    colId: StagingGridColumnId.ASSET_TYPE,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.assetType,
    width: 80
  },
  {
    ...BASE_ROE,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.rightOfExit,
    cellRendererParams: {
      validationField: OrderFieldNames.ROE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: BooleanSelectbox,
      inputParams: () => ({
        withPortal: true,
        dataTestId: 'StagingOrdersGridModel-ROEBooleanSelectbox',
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.ROE
    },
    editable: isChildRow,
    enableRowGroup: true,
    lockPinned: true
  },
  {
    ...BASE_ROS,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) =>
      stagingRowDataGetter(params)?.order?.rightOfSubstitution,
    cellRendererParams: {
      validationField: OrderFieldNames.ROS,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: BooleanSelectbox,
      inputParams: () => ({
        withPortal: true,
        dataTestId: 'StagingOrdersGridModel-ROSBooleanSelectbox',
        onPaste: stopPastePropagation
      }),
      updateField: OrderFieldNames.ROS
    },
    editable: isChildRow,
    enableRowGroup: true,
    lockPinned: true
  },
  {
    ...BASE_MIC,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => {
      const detail = stagingRowDataGetter(params)
      return detail?.order.marketIdentifierCode
    }
  },
  BASE_MARKET_TYPE,
  {
    ...BASE_ISSUE,
    colId: StagingGridColumnId.ISSUE,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.collateral?.issue,
    width: 80
  },
  {
    colId: StagingGridColumnId.HAIRCUT_TYPE,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.haircutType,
    valueFormatter: haircutTypeFormatter,
    headerName: Locale.translate(Translation.TC_COLLATERAL_HAIRCUT),
    equals: () => false,
    width: 150,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.HAIRCUT_TYPE,
      rowDataGetter: stagingRowDataGetter
    },
    cellEditorParams: {
      inputComponent: HaircutTypeSelectbox,
      inputParams: (_orderDetail: OrderDetail) => ({
        onPaste: stopPastePropagation,
        canClear: true,
        withPortal: true
      }),
      updateField: OrderFieldNames.HAIRCUT_TYPE,
      rowDataGetter: stagingRowDataGetter
    },
    lockPinned: true,
    editable: canEditHaircut,
    filter: 'agSetColumnFilter'
  },
  {
    colId: StagingGridColumnId.HAIRCUT,
    valueGetter: (params: TypedValueGetterParams<OrderDetail, OrderDetailSummary>) => stagingRowDataGetter(params)?.order?.haircut,
    headerName: Locale.translate(Translation.TC_HAIRCUT),
    equals: () => false,
    width: 80,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: (params: TypedValueFormatterParams<OrderDetail, number | undefined, OrderDetailSummary>) => {
      const data = stagingRowDataGetter(params as any)
      return makeHaircutFormatter()({ ...params, data: { ...data, ownOrder: true, direction: data?.order?.direction } })
    },
    filterValueGetter: makeHaircutFilterGetter(),
    getQuickFilterText: makeHaircutQuickFilterTextGetter(),
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: OrderFieldNames.HAIRCUT,
      rowDataGetter: stagingRowDataGetter
    },
    editable: canEditHaircut,
    cellEditorParams: {
      inputComponent: NumericInput,
      inputParams: (orderDetail: OrderDetail, context: TypedContext<OrderDetail>) => ({
        formatOptions: getHaircutFormatOptions(undefined, context.userPreference.haircutSign, true, orderDetail.order.direction),
        onPaste: stopPastePropagation
      }),
      rowDataGetter: stagingRowDataGetter,
      updateField: OrderFieldNames.HAIRCUT
    },
    lockPinned: true,
    enableRowGroup: false
  }
]
  .map(colDef => ({
    ...colDef,
    hide: true
  }))
  .sort((a, b) => a.headerName.localeCompare(b.headerName))

export const getStagingGridColumnDefs: (isLockFixOrganisation: boolean) => (ColDef | ColGroupDef)[] = isLockFixOrganisation => {
  return getVisibleColumns(isLockFixOrganisation).concat(HIDDEN_COLUMNS)
}

export const stagingGridOptions: GridOptions<OrderDetail> = {
  ...gridOptionsBase,
  masterDetail: true,
  groupDefaultExpanded: 0,
  getRowHeight: getDynamicDetailRowHeight((data: OrderDetail) => data?.order?.nestedLegs?.legs?.length),
  getRowId: ({ data }) => data.order.orderId,
  isRowMaster: orderDetail => !!orderDetail?.order?.nestedLegs?.legs,
  detailCellRenderer: StagingPreAssignmentsSubGrid
}
